import type { ID } from '../types';
import { Set } from 'immutable';
import BaseObject from './BaseObject';
import Contact from './Contact';
import Occasion, { timelineDateDisplay } from '@support/Occasion';
import { markdownToPlainText } from '@components/TextEditor/utils';
import { markdownToMentionedContactIDs } from '@components/TextEditor/mention';
import {
  filterFollowupFromMarkdown,
  markdownIncludesFollowup,
} from '@components/TextEditor/FollowupPlugin/utils/markdown';

export type NoteIconType =
  | 'airplane'
  | 'binoculars'
  | 'book'
  | 'bookmark'
  | 'briefcase'
  | 'building'
  | 'cake'
  | 'call'
  | 'car'
  | 'cloud'
  | 'cube'
  | 'cup'
  | 'desktopcomputer'
  | 'dollarsign'
  | 'drive'
  | 'eat'
  | 'flag'
  | 'flower'
  | 'game'
  | 'hand'
  | 'health'
  | 'heart'
  | 'hourglass'
  | 'inbox'
  | 'key'
  | 'leaf'
  | 'lightbulb'
  | 'map'
  | 'message'
  | 'mic'
  | 'moon'
  | 'music'
  | 'navigation'
  | 'paintbrush'
  | 'paw'
  | 'personframe'
  | 'photo'
  | 'piechart'
  | 'rolodex'
  | 'shield'
  | 'smartphone'
  | 'smile'
  | 'star'
  | 'sun'
  | 'video'
  | 'watch'
  | 'water';

type NoteColor = string | null;
type NoteIcon = NoteIconType | null;
type NoteLabel = string | null;
type NoteInternalID = 'apple-contacts' | undefined;
type NoteImportSource = 'Apple Contact' | null;

export interface NoteType {
  color: NoteColor;
  icon: NoteIcon;
  label: NoteLabel;
}

export type NoteReminder = null | { id: ID; date: Occasion };

export function removeNotePrompt(callback) {
  return (e) => {
    e.stopPropagation();
    e.preventDefault();

    window.app.confirm(
      {
        title: 'Remove Note',
        message: 'Are you sure you want to delete this note?',
        action: 'Yes, Delete',
        variant: 'danger',
      },
      (confirmed) => {
        if (!confirmed) {
          return;
        }

        callback();
      },
    );
  };
}

const reminderFromProps = ({ reminder }): NoteReminder => {
  if (reminder?.date) {
    return {
      id: reminder.id ? String(reminder.id) : null,
      date: new Occasion(reminder.date),
    };
  }

  return null;
};

export default class Note extends BaseObject {
  content: string;
  contact: Contact;
  created: Occasion;
  reminder: NoteReminder;
  type: NoteType;
  internal_id: NoteInternalID;
  is_locked: boolean;

  constructor(props: any = {}) {
    super(props);

    this.content = props.content || props.note || '';
    this.contact = new Contact(props.contact);
    this.created = new Occasion(props.created);
    this.updated = new Occasion(props.updated);
    this.reminder = reminderFromProps(props);
    this.type = { color: null, icon: null, label: null, ...(props.type || {}) };
    this.is_locked = props.metadata?.is_locked || props.is_locked || false;
    this.internal_id = props.internal_id || props.metadata?.internal_id || null;

    if (this.type.label === '') {
      this.type.label = null;
    }

    // If we encounter a note that has no associated reminder
    // but does have a span in its content that denotes a reminder,
    // we remove the span here
    if (props.reminder === null && this.hasFollowupEntity) {
      this.content = filterFollowupFromMarkdown(this.content);
    }
  }

  updateReminder(date: Occasion) {
    let reminder = this.reminder;
    let content = this.content;

    if (reminder) {
      reminder.date = date;
      content = filterFollowupFromMarkdown(content);
    } else {
      reminder = { id: null, date };
    }

    return this.copyWith({ reminder, content });
  }

  removeReminder() {
    return this.copyWith({ reminder: null });
  }

  hasSameValues(other: Note): boolean {
    if (!other) {
      return false;
    }

    let reminderDate = this.reminder?.date?.toString?.();
    let otherReminderDate = other.reminder?.date?.toString?.();

    return (
      this.id === other.id &&
      this.content === other.content &&
      reminderDate === otherReminderDate &&
      this.type?.color === other.type?.color &&
      this.type?.icon === other.type?.icon &&
      this.type?.label === other.type?.label
    );
  }

  get updatedMoreThanOneDayAgo() {
    const oneDayAfterCreateDate = new Date(this.created.date);
    oneDayAfterCreateDate.setDate(oneDayAfterCreateDate.getDate() + 1);
    return !this.updated.isInRange(this.created.date, oneDayAfterCreateDate);
  }

  get createDateDisplay() {
    return timelineDateDisplay(this.created);
  }

  get updateDateDisplay() {
    return timelineDateDisplay(this.updated);
  }

  get plaintext() {
    return markdownToPlainText(this.content);
  }

  get mentions(): Set<ID> {
    return markdownToMentionedContactIDs(this.content);
  }

  get hasFollowupEntity() {
    return markdownIncludesFollowup(this.content);
  }

  get hasReminderButNoFollowupEntity() {
    return !!this.reminder && !this.hasFollowupEntity;
  }

  get isEmpty() {
    return (
      this.content.trim() === '' &&
      !this.reminder?.date &&
      !this.type?.color &&
      !this.type?.icon &&
      !this.type?.label
    );
  }

  get isReadOnly() {
    return this.is_locked;
  }

  get importSource(): NoteImportSource {
    switch (this.internal_id) {
      case 'apple-contacts':
        return 'Apple Contact';

      default:
        return undefined;
    }
  }

  get toObj() {
    return { ...this, contact: this.contact.toObj };
  }
}
